/**
 * Subtitle store (Zustand) — F1 host + F2 viewer subtitle rendering.
 *
 * Consumes translation events and folds them into a stable list of lines:
 *  - `translation.partial` → replace the text of the CURRENT (in-progress)
 *    segment identified by `segment_id`; no flicker, same line updates in place.
 *  - `translation.final`  → mark that segment final and start a fresh line for
 *    the next segment.
 *
 * Lines are keyed by `segment_id`, so out-of-order partials for the same
 * segment still land on the right line.
 *
 * F2: the store is now INSTANTIABLE via `createSubtitleStore()`. Each consumer
 * (the host console, and every viewer page) gets its own isolated store, so a
 * viewer's lines never bleed into the host's and vice versa. `useSubtitleStore`
 * remains a shared global instance for the host console (unchanged behaviour).
 */

import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { EventEnvelopeV1 } from '@/shared/models/events';

/** One rendered subtitle line. */
export interface SubtitleLine {
  segmentId: string;
  text: string;
  /** Original transcribed source text (from translation.final input_transcript). */
  original: string | null;
  /** True once the matching translation.final arrived. */
  final: boolean;
  sourceLang: string;
  targetLang: string;
  timestampMs: number;
}

export interface SubtitleState {
  lines: SubtitleLine[];
  /** Fold a translation.partial / translation.final envelope into the lines. */
  ingest: (event: EventEnvelopeV1) => void;
  clear: () => void;
}

/** A bound Zustand store hook for subtitle state. */
export type SubtitleStore = UseBoundStore<StoreApi<SubtitleState>>;

function upsertLine(
  lines: SubtitleLine[],
  event: Extract<
    EventEnvelopeV1,
    { event_type: 'translation.partial' | 'translation.final' }
  >,
  final: boolean,
): SubtitleLine[] {
  const idx = lines.findIndex((l) => l.segmentId === event.segment_id);
  // Only translation.final carries input_transcript (the original source text).
  const original =
    event.event_type === 'translation.final'
      ? (event.payload.input_transcript ?? null)
      : (idx === -1 ? null : lines[idx].original);
  const next: SubtitleLine = {
    segmentId: event.segment_id,
    text: event.payload.text,
    original,
    final,
    sourceLang: event.source_lang,
    targetLang: event.target_lang,
    timestampMs: event.timestamp_ms,
  };
  if (idx === -1) {
    return [...lines, next];
  }
  const copy = lines.slice();
  copy[idx] = next;
  return copy;
}

/**
 * Build a fresh, isolated subtitle store. Call once per consumer (host or a
 * given viewer page) and hold the returned hook in a ref/memo.
 */
export function createSubtitleStore(): SubtitleStore {
  return create<SubtitleState>((set) => ({
    lines: [],
    ingest: (event) =>
      set((state) => {
        if (event.event_type === 'translation.partial') {
          return { lines: upsertLine(state.lines, event, false) };
        }
        if (event.event_type === 'translation.final') {
          return { lines: upsertLine(state.lines, event, true) };
        }
        return state;
      }),
    clear: () => set({ lines: [] }),
  }));
}

/** Shared global instance used by the host console (unchanged behaviour). */
export const useSubtitleStore: SubtitleStore = createSubtitleStore();
